/** One box, one button, and three plain choices about what to outline.
 *
 *  Field feedback 2026-07-25, verbatim: "It should be that we enter any semblance
 *  of a name of the HOA or address, and press Enter. It centers the map."  and
 *  "right now it's outlining a single residence inside that area."  and
 *  "These landscapers are not technical people."
 *
 *  So: Enter searches. A community name outlines the community, not a house. A
 *  lookup that lands on one lot SAYS it landed on one lot and offers the
 *  community instead — it never quietly draws the wrong thing (§2: nothing the
 *  app produces on its own is treated as confirmed).
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { getRecord, putRecord } from '../../core/db';
import { ServiceError } from '../../core/net';
import { geocodeMany, type GeocodeHit } from '../../providers/geocode';
import {
  searchSubdivisions, loadSubdivisionBoundary, boundaryCenter, boundaryZoom,
  type SubdivisionHit,
} from '../../providers/subdivision';
import { lookupParcel, looksLikeOneHouse } from '../../providers/parcel';
import type { Job } from '../../core/types';
import { toast } from '../state';

interface Props {
  job: Job;
  picking: boolean;
  onPicking: (on: boolean) => void;
  onChanged: () => Promise<void>;
}

interface Note {
  kind: 'error' | 'info';
  text: string;
  /** an offer to fix what we just found, e.g. "outline the whole community" */
  action?: { label: string; run: () => Promise<void> };
}

export function FindBar({ job, picking, onPicking, onChanged }: Props) {
  const [q, setQ] = useState(job.siteAddress);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note | null>(null);
  const [places, setPlaces] = useState<SubdivisionHit[]>([]);
  const [addresses, setAddresses] = useState<GeocodeHit[]>([]);
  const [open, setOpen] = useState(false);
  const [searched, setSearched] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setQ(job.siteAddress); }, [job.id]);

  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  /** Both lookups run together; either can fail without killing the other. */
  async function search(autoLocate = true) {
    const text = q.trim();
    if (text.length < 3) {
      setNote({ kind: 'info', text: 'Type at least three characters — a community name, an address, or two cross streets.' });
      return;
    }
    setBusy(true); setNote(null); setSearched(false);
    const [subs, addrs] = await Promise.allSettled([searchSubdivisions(text), geocodeMany(text)]);

    const gotSubs = subs.status === 'fulfilled' ? subs.value : [];
    const gotAddrs = addrs.status === 'fulfilled' ? addrs.value : [];
    setPlaces(gotSubs);
    setAddresses(gotAddrs);
    setSearched(true);
    setOpen(gotSubs.length + gotAddrs.length > 1);
    setBusy(false);

    if (autoLocate) {
      // A community name gets the community. A street address gets the address.
      // The leading-digit test is what keeps "1234 Greenway Rd" from outlining a
      // whole subdivision that happens to share the word.
      const namedAPlace = !/^\s*\d/.test(text);
      if (namedAPlace && gotSubs[0]) { void choosePlace(gotSubs[0], gotSubs.length > 1); return; }
      const top = gotAddrs[0];
      if (top) {
        // Field report 2026-07-27: "I should just be able to put in the address
        // or property name and it automatically do that." Centring the map and
        // then waiting to be told which of four buttons applied was the whole
        // complaint. An address means a property, so outline the property.
        await centreOn(top);
        await outlineOneProperty({ quiet: gotAddrs.length > 1 || gotSubs.length > 0 });
        return;
      }
      if (gotSubs[0]) { void choosePlace(gotSubs[0], gotSubs.length > 1); return; }
    }

    if (!gotSubs.length && !gotAddrs.length) {
      const why = [subs, addrs]
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
        .map((r) => (r.reason instanceof ServiceError ? r.reason.message : ''))
        .filter(Boolean)[0];
      setNote({
        kind: 'info',
        text: why
          ? `${why} You can still zoom the map to the property and use “Tap the map”.`
          : 'Nothing found by that name or address. Try fewer words — “Greenway Parc” instead of “Greenway Parc 3 Surprise AZ” — or use “Tap the map”, which needs no typing at all.',
      });
      setOpen(false);
    }
  }

  /** offerOthers: several plats share the name, so outline the best one straight away
   *  — the field asked for Enter to actually do something — but leave the alternatives
   *  on screen instead of making the user guess how to get the list back. */
  async function choosePlace(hit: SubdivisionHit, offerOthers = false) {
    setBusy(true); setOpen(offerOthers); setNote(null);
    try {
      const parcel = await loadSubdivisionBoundary(hit);
      const [lat, lng] = boundaryCenter(parcel.rings);
      const fresh = (await getRecord('jobs', job.id))!;
      await putRecord('jobs', {
        ...fresh, parcel, lat, lng, zoom: boundaryZoom(parcel.rings),
        // the plat label carries the identity; siteAddress stays the street address
        title: fresh.title.startsWith('Job B-') ? hit.name : fresh.title,
      });
      await onChanged();
      toast(`${hit.name} — ${(parcel.lotSqFt / 43560).toFixed(1)} acres outlined. Not verified until you confirm it against what the client actually owns.`);
      if (offerOthers) {
        setNote({ kind: 'info', text: `Outlined ${hit.name}. The county records other plats with a similar name — they are listed above if this is the wrong one.` });
      }
    } catch (e) {
      setNote({
        kind: 'error',
        text: e instanceof ServiceError ? `${e.message} You can draw the boundary by hand instead.` : 'That boundary could not be loaded. You can draw it by hand instead.',
      });
    } finally { setBusy(false); }
  }

  /** move the map to a geocoded hit and record the address on the job */
  async function centreOn(hit: GeocodeHit) {
    const fresh = (await getRecord('jobs', job.id))!;
    await putRecord('jobs', { ...fresh, siteAddress: hit.display, lat: hit.lat, lng: hit.lng, zoom: 19 });
    await onChanged();
  }

  async function chooseAddress(hit: GeocodeHit) {
    setBusy(true); setOpen(false); setNote(null);
    try {
      await centreOn(hit);
      await outlineOneProperty();
    } finally { setBusy(false); }
  }

  /** field feedback: a wrong parcel loaded and there was no way to get rid of it */
  async function clearBoundary() {
    setBusy(true); setNote(null);
    try {
      const fresh = (await getRecord('jobs', job.id))!;
      await putRecord('jobs', { ...fresh, parcel: null });
      await onChanged();
      toast('Outline removed.');
    } finally { setBusy(false); }
  }

  /** Outline the community. Works from whatever we know: the plat already loaded,
   *  the subdivision the pinned parcel sits in, or the text in the box. */
  async function outlineCommunity() {
    setBusy(true); setNote(null);
    try {
      const seed = job.parcel?.subname || q.trim();
      if (!seed) {
        setNote({ kind: 'info', text: 'Type the community name in the box first, then press Find.' });
        return;
      }
      const hits = await searchSubdivisions(seed);
      if (!hits.length) {
        setNote({
          kind: 'info',
          text: `The county has no recorded plat matching “${seed}”. Try fewer words, or outline the areas by hand — everything downstream works the same.`,
        });
        return;
      }
      if (hits.length > 1) { setPlaces(hits); setSearched(true); setOpen(true); }
      await choosePlace(hits[0]!);
    } catch (e) {
      setNote({
        kind: 'error',
        text: e instanceof ServiceError ? `${e.message} You can draw the boundary by hand instead.` : 'The county lookup failed. You can draw the boundary by hand instead.',
      });
    } finally { setBusy(false); }
  }

  /** Outline the single parcel under the pin — and say plainly when that is a house. */
  async function outlineOneProperty(opts?: { quiet?: boolean }) {
    setBusy(true); setNote(null);
    try {
      // read the job back: the address path has just moved the pin, and the copy
      // this render closed over still has where it used to be
      const at = (await getRecord('jobs', job.id))!;
      if (at.lat == null || at.lng == null) {
        setNote({ kind: 'info', text: 'Find the property first, or use “Tap the map” to point at it.' });
        return;
      }
      const r = await lookupParcel(at.lat, at.lng);
      if (!r.ok) { setNote({ kind: r.reason === 'no_coverage' ? 'info' : 'error', text: r.message }); return; }
      const parcel = r.parcel;
      const fresh = (await getRecord('jobs', job.id))!;
      await putRecord('jobs', { ...fresh, parcel });
      await onChanged();

      const sqft = parcel.lotSqFt.toLocaleString('en-US');
      if (looksLikeOneHouse(parcel)) {
        // This is the defect Phil hit: it drew one house and said nothing.
        setNote({
          kind: 'info',
          text: `That is one lot — ${sqft} sq ft${parcel.lotNum ? `, lot ${parcel.lotNum}` : ''}, inside ${parcel.subname}. If you are bidding the whole community, outline that instead.`,
          action: {
            label: `Outline all of ${parcel.subname}`,
            run: async () => {
              const hits = await searchSubdivisions(parcel.subname!);
              if (hits[0]) await choosePlace(hits[0]);
              else setNote({ kind: 'info', text: `The county has no plat boundary filed under ${parcel.subname}. Outline the areas by hand instead.` });
            },
          },
        });
      } else {
        toast(`Outlined ${sqft} sq ft — APN ${parcel.apn}. Unverified until you walk it.`);
        if (opts?.quiet) {
          setNote({ kind: 'info', text: 'Other matches are listed above — pick one if this is the wrong property.' });
        }
      }
    } catch (e) {
      setNote({
        kind: 'error',
        text: `${e instanceof ServiceError ? e.message : 'The county lookup failed.'} Drawing and pricing still work.`,
      });
    } finally { setBusy(false); }
  }

  const hasResults = places.length > 0 || addresses.length > 0;

  return (
    <div ref={boxRef} style="position:relative;">
      <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;">
        <input
          value={q} aria-label="Find the property"
          placeholder="Community name, address, or cross streets"
          style="flex:1;min-width:210px;min-height:36px;"
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
          onFocus={() => { if (hasResults) setOpen(true); }}
          onKeyDown={(e) => { if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); void search(true); } }}
        />
        <button class="btn btn-small btn-primary" disabled={busy} onClick={() => void search()}>
          {busy ? 'Looking…' : 'Find'}
        </button>
      </div>

      {/*
        * Field report 2026-07-27: "I don't see much value in outlining the whole
        * community, one property, tap the map, or remove outline. I should just
        * be able to put in the address or property name and it automatically do
        * that."
        *
        * Largely right. Typing and pressing Enter already outlines the thing it
        * found — those four buttons were four ways to do what the search had
        * done, presented before it had done anything, to a person who has no
        * reason to know which one applies.
        *
        * What survives is the one case the search genuinely cannot settle: a
        * street address is a house, and a house sits inside a subdivision, so
        * "did you mean this lot or the whole community" is a real question with
        * no right default. It is now asked once, after the fact, about a named
        * thing on screen — not offered as a menu beforehand.
        *
        * Drawing by hand stays as a fallback, because ground with no address —
        * a retention basin, a strip between two streets — has to be reachable.
        */}
      {job.parcel && (
        <div class="outlinerow" style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:8px;">
          <span class="small" aria-label="What is outlined">
            Outlined: <b>{outlineLabel(job.parcel)}</b>
          </span>
          {job.parcel.provider === 'maricopa' && (
            <button class="btn btn-small btn-ghost" disabled={busy} onClick={() => void outlineCommunity()}
              title="Outline the whole subdivision this lot sits in">
              Use the whole community instead
            </button>
          )}
          {job.parcel.provider === 'maricopa-subdivision' && (
            <button class="btn btn-small btn-ghost" disabled={busy} onClick={() => void outlineOneProperty()}
              title="Outline just the county parcel under the middle of the map">
              Use one property instead
            </button>
          )}
          <button class="btn btn-small btn-ghost" disabled={busy} onClick={() => void clearBoundary()}
            title="Remove the outline and start over">Start over</button>
        </div>
      )}

      {!job.parcel && (
        <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:8px;">
          <span class="small muted">
            Type a community name or an address and press Enter — it outlines what it finds.
          </span>
          <button class={`btn btn-small ${picking ? 'btn-primary' : 'btn-ghost'}`} disabled={busy}
            aria-pressed={picking} onClick={() => onPicking(!picking)}
            title="For ground with no address — a basin, a strip between two streets">
            {picking ? 'Now click the map…' : 'No address? Tap the map'}
          </button>
        </div>
      )}

      {open && hasResults && (
        <div class="findresults" role="listbox" aria-label="Search results">
          {places.length > 0 && (
            <>
              <div class="findhead">Communities — county plat boundaries</div>
              {places.slice(0, 8).map((p) => (
                <button key={`${p.name}|${p.mcr}`} class="findrow" role="option" aria-selected="false"
                  onClick={() => void choosePlace(p)}>
                  <b>{p.name}</b>
                  <span class="findsub">Recorded plat{p.mcr ? ` · MCR ${p.mcr}` : ''} — outlines the whole community</span>
                </button>
              ))}
            </>
          )}
          {addresses.length > 0 && (
            <>
              <div class="findhead">Addresses &amp; places</div>
              {addresses.slice(0, 6).map((a) => (
                <button key={a.display} class="findrow" role="option" aria-selected="false"
                  onClick={() => void chooseAddress(a)}>
                  {a.display}
                  <span class="findsub">Centers the map here</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {searched && places.length === 0 && addresses.length > 0 && open && (
        <p class="small muted" style="margin-top:4px;">No county plat matches that name — the addresses above are from OpenStreetMap.</p>
      )}

      {picking && (
        <div class="banner banner-info" style="margin:8px 0 0;" role="status">
          <div>Click the spot on the map. The county parcel under your click loads automatically — no address needed.</div>
          <button class="btn btn-small btn-ghost" onClick={() => onPicking(false)}>Cancel</button>
        </div>
      )}

      {note && (
        <div class={`banner ${note.kind === 'info' ? 'banner-info' : ''}`} style="margin:8px 0 0;"
          role={note.kind === 'error' ? 'alert' : 'status'}>
          <div>{note.text}</div>
          <div style="display:flex;gap:6px;flex-wrap:wrap;">
            {note.action && (
              <button class="btn btn-small btn-primary" disabled={busy}
                onClick={() => { const run = note.action!.run; setNote(null); setBusy(true); void run().finally(() => setBusy(false)); }}>
                {note.action.label}
              </button>
            )}
            <button class="btn btn-small btn-ghost" onClick={() => setNote(null)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
}

export { }; // module marker

/** What is on screen, said the way David would say it. */
function outlineLabel(p: { provider: string; label?: string; address?: string; apn: string; subname?: string }): string {
  if (p.provider === 'maricopa-subdivision') return (p.label ?? '').split(' \u00b7 ')[0] || 'this community';
  if (p.provider === 'maricopa') {
    const where = p.subname ? ` in ${p.subname}` : '';
    return `${p.address || `parcel ${p.apn}`}${where}`;
  }
  return p.label || 'a boundary you drew';
}
